/* Colour is assigned here and nowhere else.
 *
 * A status that is amber in the queue and green on the ticket is how people
 * stop trusting a status. One function per vocabulary, used by every surface.
 * The pairings come from the queue mockup: New reads as purple/new, In progress
 * as amber/live, Waiting as grey/parked, Resolved as green/done.
 */

export function statusTone(status) {
  return (
    {
      new: 'info',
      in_progress: 'warn',
      waiting: 'flat',
      resolved: 'good',
      closed: 'flat',
    }[status] || 'flat'
  );
}

export function slaTone(state) {
  return { met: 'good', ok: 'flat', at_risk: 'warn', breached: 'error' }[state] || 'flat';
}

export function slaLabel(state) {
  return { met: 'Met', ok: 'On track', at_risk: 'At risk', breached: 'Breached' }[state] || state;
}

/** "3 hours ago" · "yesterday" · "on 4 Aug" — relative until it stops helping. */
export function since(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** "08:31" for today, "yesterday" or "14 Aug" beyond it — the queue's `raised`. */
export function shortWhen(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const sameDay = then.toDateString() === now.toDateString();
  if (sameDay) return then.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const days = Math.round((now - then) / 86400000);
  if (days <= 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function stamp(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Just the clock, for messages inside a thread that is already dated. */
export function clockTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Turn a stored option value back into the label the requester actually saw.
 *
 * A `person` field holds either a directory record picked from the lookup or the
 * plain string somebody typed, so both shapes have to read back. This is the ONE
 * place that conversion happens — the receipt, the ticket detail table and the
 * resolution summary all come through here, and seeded demo tickets that still
 * hold plain strings keep rendering unchanged. */
export function labelFor(field, value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value.displayName || value.mail || '';
  }
  /* A multi-select holds an ARRAY, and React renders an array by gluing its
   * items together with nothing between them. `['light', 'sop_docs']` reached
   * the receipt as "lightsop_docs" and `['m365', 'asana']` as "m365asana" -
   * unreadable, and it read as a bug because it was one. Each value becomes the
   * label the person actually saw, joined the way a person writes a list. */
  if (Array.isArray(value)) {
    return value.map((v) => labelFor(field, v)).filter(Boolean).join(', ');
  }
  if (!field) return value;
  /* An acknowledgement stores WHEN it was agreed rather than `true`, so IT can
   * say what was agreed and when. An ISO timestamp is the right thing to store
   * and the wrong thing to show somebody on their own receipt. */
  if (field.type === 'ack') return value ? `Agreed - ${stamp(value)}` : '';
  const opt = (field.options || []).find((o) => o.value === value);
  return opt ? opt.label : value;
}
